import React, {
    useEffect,
    useState
} from "react";

import {
    useNavigate,
    useParams
} from "react-router-dom";

import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";

import QuillEditor from "../components/QuillEditor";


const AddCategory = () => {

    const navigate = useNavigate();

    const {
        id
    } = useParams();


    // =========================================================
    // EDIT MODE
    // =========================================================

    const isEditMode = Boolean(id);


    // =========================================================
    // FORM DATA
    // =========================================================

    const [formData, setFormData] = useState({

        paper: "",

        price: "",

        duration: "",

        link: "",

        detail1: "",

        detail2: "",

        detail3: "",

        detail4: "",

        negative_marks: "0",

        marks: "0",

        show_on_home: "No",

        status: "Enable"
    });


    // =========================================================
    // LOADING
    // =========================================================

    const [loading, setLoading] = useState(false);


    const [pageLoading, setPageLoading] = useState(false);


    // =========================================================
    // HANDLE NORMAL INPUT
    // =========================================================

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;


        setFormData((prev) => ({

            ...prev,

            [name]: value
        }));
    };


    // =========================================================
    // HANDLE QUILL
    // =========================================================

    const handleQuillChange = (
        field,
        value
    ) => {

        setFormData((prev) => ({

            ...prev,

            [field]: value || ""
        }));
    };


    // =========================================================
    // LOAD CATEGORY FOR EDIT
    // =========================================================

    useEffect(() => {

        if (!isEditMode) {

            return;
        }


        const fetchCategory = async () => {

            try {

                setPageLoading(true);


                const response = await fetch(
                    `${API_BASE_URL}/admin/paper-category/${id}`,
                    {
                        method: "GET",

                        credentials: "include",

                        headers: {
                            "Accept": "application/json"
                        }
                    }
                );


                const data = await response.json();


                if (!response.ok) {

                    throw new Error(
                        data?.message ||
                        "Failed to load paper category."
                    );
                }


                const category =
                    data?.data || data;


                setFormData({

                    paper:
                        category.paper ??
                        "",

                    price:
                        category.price ??
                        "",

                    duration:
                        category.duration ??
                        "",

                    link:
                        category.link ??
                        "",

                    detail1:
                        category.detail1 ??
                        "",

                    detail2:
                        category.detail2 ??
                        "",

                    detail3:
                        category.detail3 ??
                        "",

                    detail4:
                        category.detail4 ??
                        "",

                    negative_marks:
                        category.negative_marks ??
                        "0",

                    marks:
                        category.marks ??
                        "0",

                    show_on_home:
                        category.show_on_home ??
                        "No",

                    status:
                        category.status ??
                        "Enable"
                });


            } catch (error) {

                console.error(
                    "LOAD CATEGORY ERROR:",
                    error
                );


                Swal.fire({

                    icon: "error",

                    title: "Error",

                    text:
                        error.message ||
                        "Failed to load paper category."
                });

            } finally {

                setPageLoading(false);
            }
        };


        fetchCategory();

    }, [
        id,
        isEditMode
    ]);


    // =========================================================
    // VALIDATION
    // =========================================================

    const validateForm = () => {

        if (!formData.paper.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Paper Name Required",

                text: "Please enter paper name."
            });

            return false;
        }


        if (
            formData.price === "" ||
            Number(formData.price) < 0
        ) {

            Swal.fire({

                icon: "warning",

                title: "Invalid Price",

                text: "Please enter a valid price."
            });

            return false;
        }


        if (!formData.duration.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Duration Required",

                text: "Please enter paper duration."
            });

            return false;
        }


        if (
            formData.negative_marks === "" ||
            Number(formData.negative_marks) < 0
        ) {

            Swal.fire({

                icon: "warning",

                title: "Invalid Negative Marks",

                text:
                    "Please enter valid negative marks."
            });

            return false;
        }


        if (!String(formData.marks).trim()) {

            Swal.fire({

                icon: "warning",

                title: "Marks Required",

                text: "Please enter marks."
            });

            return false;
        }


        return true;
    };


    // =========================================================
    // SUBMIT
    // =========================================================

    const handleSubmit = async (e) => {

        e.preventDefault();


        if (!validateForm()) {

            return;
        }


        try {

            setLoading(true);


            const payload = {

                paper:
                    formData.paper.trim(),

                price:
                    Number(formData.price),

                duration:
                    formData.duration.trim(),

                link:
                    formData.link?.trim() || "",

                detail1:
                    formData.detail1 || "",

                detail2:
                    formData.detail2 || "",

                detail3:
                    formData.detail3 || "",

                detail4:
                    formData.detail4 || "",

                negative_marks:
                    Number(
                        formData.negative_marks
                    ),

                marks:
                    String(
                        formData.marks
                    ).trim(),

                show_on_home:
                    formData.show_on_home,

                status:
                    formData.status
            };


            console.log(
                "Paper Category Payload:",
                payload
            );


            const url = isEditMode

                ? `${API_BASE_URL}/admin/paper-category/${id}`

                : `${API_BASE_URL}/admin/paper-category`;


            const response = await fetch(
                url,
                {

                    method:
                        isEditMode
                            ? "PUT"
                            : "POST",

                    credentials: "include",

                    headers: {

                        "Content-Type":
                            "application/json",

                        "Accept":
                            "application/json"
                    },

                    body:
                        JSON.stringify(
                            payload
                        )
                }
            );


            // =================================================
            // READ RESPONSE SAFELY
            // =================================================

            const responseText =
                await response.text();


            let data = {};

            try {

                data =
                    responseText
                        ? JSON.parse(
                            responseText
                        )
                        : {};

            } catch (jsonError) {

                console.error(
                    "Invalid JSON Response:",
                    responseText
                );
            }


            console.log(
                "Server Response:",
                data
            );


            // =================================================
            // VALIDATION ERROR
            // =================================================

            if (
                response.status === 422
            ) {

                let errorMessage =
                    data?.message ||
                    "Validation failed.";


                if (
                    data?.errors
                ) {

                    const errors =
                        Object.values(
                            data.errors
                        )
                            .flat()
                            .join("\n");


                    if (errors) {

                        errorMessage =
                            errors;
                    }
                }


                Swal.fire({

                    icon: "warning",

                    title: "Validation Error",

                    text: errorMessage
                });


                return;
            }


            // =================================================
            // UNAUTHORIZED
            // =================================================

            if (
                response.status === 401 ||
                response.status === 419
            ) {

                Swal.fire({

                    icon: "warning",

                    title: "Session Expired",

                    text:
                        "Your login session has expired. Please login again."
                });


                return;
            }


            // =================================================
            // SERVER ERROR
            // =================================================

            if (!response.ok) {

                let errorMessage =
                    data?.error ||
                    data?.message ||
                    responseText ||
                    `Server Error (${response.status})`;


                console.error(
                    "Paper Category Server Error:",
                    {
                        status:
                            response.status,

                        data,

                        responseText
                    }
                );


                Swal.fire({

                    icon: "error",

                    title: "Server Error",

                    text: errorMessage,

                    confirmButtonText:
                        "OK"
                });


                return;
            }


            // =================================================
            // SUCCESS
            // =================================================

            Swal.fire({

                icon: "success",

                title: "Success",

                text:
                    data?.message ||
                    (
                        isEditMode
                            ? "Paper category updated successfully."
                            : "Paper category added successfully."
                    ),

                confirmButtonText:
                    "OK"
            }).then(() => {

                navigate(
                    "/paper-category"
                );
            });


        } catch (error) {

            console.error(
                "ADD/UPDATE CATEGORY ERROR:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    error?.message ||
                    "Failed to save paper category."
            });

        } finally {

            setLoading(false);
        }
    };


    // =========================================================
    // PAGE LOADING
    // =========================================================

    if (pageLoading) {

        return (

            <div
                className="container-fluid py-4"
            >

                <div
                    className="card shadow-sm"
                >

                    <div
                        className="card-body text-center py-5"
                    >

                        <div
                            className="spinner-border text-primary"
                            role="status"
                        />

                        <div
                            className="mt-3"
                        >
                            Loading paper category...
                        </div>

                    </div>

                </div>

            </div>
        );
    }


    // =========================================================
    // UI
    // =========================================================

    return (

        <div
            className="container-fluid"
            style={{
                maxHeight:
                    "85vh",

                overflowY:
                    "auto"
            }}
        >

            <div
                className="card shadow-sm border-0"
            >

                {/* =================================================
                    HEADER
                ================================================= */}

                <div
                    className="card-header bg-white d-flex justify-content-between align-items-center"
                >

                    <h4
                        className="mb-0"
                    >
                        {isEditMode
                            ? "Edit Paper Category"
                            : "Add Paper Category"}
                    </h4>


                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                            navigate(
                                "/paper-category"
                            )
                        }
                        disabled={loading}
                    >
                        Back
                    </button>

                </div>


                {/* =================================================
                    BODY
                ================================================= */}

                <div
                    className="card-body"
                >

                    <form
                        onSubmit={handleSubmit}
                    >

                        {/* =================================================
                            PAPER
                        ================================================= */}

                        <div
                            className="mb-3"
                        >

                            <label
                                className="form-label fw-semibold"
                            >
                                Paper Name
                                <span className="text-danger">
                                    *
                                </span>
                            </label>


                            <input
                                type="text"
                                name="paper"
                                className="form-control"
                                value={
                                    formData.paper
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="Enter paper name"
                                maxLength={250}
                                disabled={loading}
                            />

                        </div>


                        {/* =================================================
                            PRICE / DURATION / LINK
                        ================================================= */}

                        <div
                            className="row"
                        >

                            <div
                                className="col-md-4 mb-3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >
                                    Price
                                </label>


                                <input
                                    type="number"
                                    name="price"
                                    className="form-control"
                                    value={
                                        formData.price
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    min="0"
                                    placeholder="0"
                                    disabled={loading}
                                />

                            </div>


                            <div
                                className="col-md-4 mb-3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >
                                    Duration
                                </label>


                                <input
                                    type="text"
                                    name="duration"
                                    className="form-control"
                                    value={
                                        formData.duration
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Example: 30 Minutes"
                                    maxLength={100}
                                    disabled={loading}
                                />

                            </div>


                            <div
                                className="col-md-4 mb-3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >
                                    Link
                                </label>


                                <input
                                    type="text"
                                    name="link"
                                    className="form-control"
                                    value={
                                        formData.link
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Enter link"
                                    maxLength={100}
                                    disabled={loading}
                                />

                            </div>

                        </div>


                        {/* =================================================
                            DETAIL 1
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="form-label fw-semibold"
                            >
                                Detail 1
                            </label>


                            <QuillEditor
                                value={
                                    formData.detail1
                                }
                                onChange={(
                                    value
                                ) =>
                                    handleQuillChange(
                                        "detail1",
                                        value
                                    )
                                }
                            />

                        </div>


                        {/* =================================================
                            DETAIL 2
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="form-label fw-semibold"
                            >
                                Detail 2
                            </label>


                            <QuillEditor
                                value={
                                    formData.detail2
                                }
                                onChange={(
                                    value
                                ) =>
                                    handleQuillChange(
                                        "detail2",
                                        value
                                    )
                                }
                            />

                        </div>


                        {/* =================================================
                            DETAIL 3
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="form-label fw-semibold"
                            >
                                Detail 3
                            </label>


                            <QuillEditor
                                value={
                                    formData.detail3
                                }
                                onChange={(
                                    value
                                ) =>
                                    handleQuillChange(
                                        "detail3",
                                        value
                                    )
                                }
                            />

                        </div>


                        {/* =================================================
                            DETAIL 4
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="form-label fw-semibold"
                            >
                                Detail 4
                            </label>


                            <QuillEditor
                                value={
                                    formData.detail4
                                }
                                onChange={(
                                    value
                                ) =>
                                    handleQuillChange(
                                        "detail4",
                                        value
                                    )
                                }
                            />

                        </div>


                        {/* =================================================
                            NEGATIVE MARKS / MARKS
                        ================================================= */}

                        <div
                            className="row"
                        >

                            <div
                                className="col-md-6 mb-3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >
                                    Negative Marks
                                </label>


                                <input
                                    type="number"
                                    name="negative_marks"
                                    className="form-control"
                                    value={
                                        formData.negative_marks
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    min="0"
                                    step="0.01"
                                    placeholder="0"
                                    disabled={loading}
                                />

                            </div>


                            <div
                                className="col-md-6 mb-3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >
                                    Marks
                                </label>


                                <input
                                    type="text"
                                    name="marks"
                                    className="form-control"
                                    value={
                                        formData.marks
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Example: 4"
                                    maxLength={20}
                                    disabled={loading}
                                />

                            </div>

                        </div>


                        {/* =================================================
                            SHOW ON HOME / STATUS
                        ================================================= */}

                        <div
                            className="row"
                        >

                            <div
                                className="col-md-6 mb-3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >
                                    Show On Home
                                </label>


                                <select
                                    name="show_on_home"
                                    className="form-select"
                                    value={
                                        formData.show_on_home
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    disabled={loading}
                                >

                                    <option value="Yes">
                                        Yes
                                    </option>

                                    <option value="No">
                                        No
                                    </option>

                                </select>

                            </div>


                            <div
                                className="col-md-6 mb-3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >
                                    Status
                                </label>


                                <select
                                    name="status"
                                    className="form-select"
                                    value={
                                        formData.status
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    disabled={loading}
                                >

                                    <option value="Enable">
                                        Enable
                                    </option>

                                    <option value="Disable">
                                        Disable
                                    </option>

                                </select>

                            </div>

                        </div>


                        {/* =================================================
                            BUTTONS
                        ================================================= */}

                        <div
                            className="d-flex gap-2 mt-4"
                        >

                            <button
                                type="submit"
                                className="btn btn-primary px-4"
                                disabled={loading}
                            >

                                {loading ? (

                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                        />

                                        Saving...
                                    </>

                                ) : (

                                    isEditMode
                                        ? "Update Category"
                                        : "Add Category"
                                )}

                            </button>


                            <button
                                type="button"
                                className="btn btn-secondary px-4"
                                onClick={() =>
                                    navigate(
                                        "/paper-category"
                                    )
                                }
                                disabled={loading}
                            >
                                Cancel
                            </button>

                        </div>

                    </form>

                </div>

            </div>

        </div>
    );
};


export default AddCategory;